<script setup>
import { ref, watch, computed } from 'vue';
import { numeroDeMoeda } from 'dashboard/helper/moeda';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  cardTitle: {
    type: String,
    default: '',
  },
  expectedRevenue: {
    type: [Number, String],
    default: null,
  },
  currency: {
    type: String,
    default: 'BRL',
  },
});

const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

const wonValue = ref('');
const wonNote = ref('');
const isSubmitting = ref(false);

const resetForm = () => {
  wonValue.value = '';
  wonNote.value = '';
  isSubmitting.value = false;
};

// Formatar valor para exibição
const formatCurrency = value => {
  if (!value) return '';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.currency || 'BRL',
  }).format(num);
};

// Inicializar com expected_revenue quando modal abrir
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      // Pré-preencher com expected_revenue se existir
      if (props.expectedRevenue) {
        wonValue.value = String(props.expectedRevenue);
      }
    } else {
      resetForm();
    }
  }
);

// Normalize BR decimal (1.234,56) to API-friendly (1234.56).
// Era a QUARTA leitura de dinheiro do produto, com a sua própria conta:
// apagava todo ponto e trocava a vírgula. Acertava o formato brasileiro e
// errava o do sistema — "1500.00" virava 150000, cem vezes mais. Agora usa o
// mesmo leitor de todos os outros campos. Ver `helper/moeda`.
const normalizeDecimal = raw => {
  const numero = numeroDeMoeda(raw);
  return numero === null ? '' : String(numero);
};

const isFormValid = computed(() => {
  const normalized = normalizeDecimal(wonValue.value);
  if (!normalized) return false;
  const num = parseFloat(normalized);
  return !Number.isNaN(num) && num > 0;
});

const handleConfirm = () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;

  emit('confirm', {
    wonValue: parseFloat(normalizeDecimal(wonValue.value)),
    wonNote: wonNote.value.trim() || null,
  });
  resetForm();
};

const handleCancel = () => {
  emit('cancel');
  resetForm();
};
</script>

<template>
  <Modal :show="show" @close="handleCancel">
    <div class="p-6 max-w-md">
      <!-- Header -->
      <div class="flex items-center gap-3 mb-6">
        <div
          class="w-10 h-10 rounded-full bg-n-teal-3 flex items-center justify-center"
        >
          <fluent-icon
            icon="checkmark-circle"
            size="20"
            class="text-n-teal-11"
          />
        </div>
        <div>
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.WON_REASON.MODAL_TITLE') }}
          </h3>
          <p
            v-if="cardTitle"
            class="text-label-small text-n-slate-11 truncate max-w-[280px]"
          >
            {{ cardTitle }}
          </p>
        </div>
      </div>

      <!-- Subtitle -->
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.WON_REASON.MODAL_SUBTITLE') }}
      </p>

      <!-- Closing Value Input (Required) -->
      <div class="mb-4">
        <label
          for="won-reason-value-input"
          class="block text-label text-n-slate-12 mb-1"
        >
          {{ t('PIPELINE.WON_REASON.VALUE_LABEL') }}
          <span class="text-n-ruby-11">*</span>
        </label>
        <div class="relative">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 text-label-small text-n-slate-11"
            aria-hidden="true"
          >
            {{ currency === 'BRL' ? 'R$' : '$' }}
          </span>
          <input
            id="won-reason-value-input"
            v-model="wonValue"
            type="text"
            inputmode="decimal"
            pattern="[0-9]*[.,]?[0-9]*"
            class="no-margin h-10 w-full pl-10 reset-base"
            :placeholder="t('PIPELINE.WON_REASON.VALUE_PLACEHOLDER')"
          />
        </div>
        <p v-if="expectedRevenue" class="text-label-small text-n-slate-11 mt-1">
          {{ t('PIPELINE.WON_REASON.EXPECTED_VALUE') }}:
          {{ formatCurrency(expectedRevenue) }}
        </p>
      </div>

      <!-- Optional Note -->
      <div class="mb-4">
        <label
          for="won-reason-note-textarea"
          class="block text-label text-n-slate-12 mb-1"
        >
          {{ t('PIPELINE.WON_REASON.NOTE_LABEL') }}
          <span class="text-label-small text-n-slate-11">
            ({{ t('PIPELINE.WON_REASON.OPTIONAL') }})
          </span>
        </label>
        <textarea
          id="won-reason-note-textarea"
          v-model="wonNote"
          class="w-full resize-none"
          :placeholder="t('PIPELINE.WON_REASON.NOTE_PLACEHOLDER')"
          rows="3"
          maxlength="500"
        />
        <p class="text-label-small text-n-slate-11 mt-1 text-right">
          {{ `${wonNote.length}/500` }}
        </p>
      </div>

      <!-- Actions -->
      <div
        class="flex items-center justify-end gap-3 pt-4 border-t border-n-weak"
      >
        <Button variant="ghost" color="slate" size="sm" @click="handleCancel">
          {{ t('PIPELINE.WON_REASON.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="teal"
          size="sm"
          :disabled="!isFormValid || isSubmitting"
          :is-loading="isSubmitting"
          @click="handleConfirm"
        >
          <fluent-icon icon="checkmark-circle" size="14" class="mr-1" />
          {{ t('PIPELINE.WON_REASON.CONFIRM') }}
        </Button>
      </div>
    </div>
  </Modal>
</template>
